import { compact } from "@/lib/format";

interface LimitBarProps {
  label: string;
  /** Secondary note shown after the label (e.g. "resets in 4h"). */
  note?: string;
  used: number;
  limit: number | null;
  unit?: string;
  color?: string;
  /**
   * The figure was reasoned about from elapsed time, not measured. Rendered
   * distinctly so a projection is never mistaken for a reading.
   */
  inferred?: boolean;
}

/**
 * A labelled meter for one usage window.
 *
 * The fill carries severity — the series accent until the window is most of the
 * way gone, then warning, then critical — while the unfilled track is a faint
 * step of the same hue, so the bar's state reads across its whole length rather
 * than only where it happens to end.
 */
export default function LimitBar({
  label,
  note,
  used,
  limit,
  unit = "",
  color = "#3987e5",
  inferred = false,
}: LimitBarProps) {
  const validLimit = limit != null && limit > 0 ? limit : null;
  const pct = validLimit ? Math.min(100, (used / validLimit) * 100) : null;

  // Thresholds are about "should I worry", not evenly spaced buckets. An
  // inferred figure keeps the series hue whatever its value: escalating a
  // projection to warning/critical would raise an alarm about a number nobody
  // measured.
  const fill =
    inferred || pct === null || pct < 75
      ? color
      : pct < 90
        ? "var(--warning)"
        : "var(--critical)";

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="min-w-0 truncate text-muted">
          {label}
          {note && <span className="text-faint"> · {note}</span>}
        </span>
        {/* Inferred values are quieted and marked with a tilde — the reader can
            tell a projection from a reading without consulting a legend. */}
        <span
          className={`tnum shrink-0 font-mono ${inferred ? "text-faint" : "text-foreground"}`}
          title={inferred ? "Assumed from the elapsed window — not measured" : undefined}
        >
          {inferred && "~"}
          {pct !== null ? (
            <>
              {Math.round(pct)}
              <span className="text-faint">%</span>
            </>
          ) : (
            <>
              {compact(used)}
              {unit && <span className="text-faint"> {unit}</span>}
            </>
          )}
        </span>
      </div>

      <div
        className="h-1.5 w-full overflow-hidden rounded-full"
        // Track is the series hue at low alpha: state reads along the whole bar.
        style={{ background: `color-mix(in srgb, ${color} 13%, var(--surface-2))` }}
        role="meter"
        aria-valuenow={pct !== null ? Math.round(pct) : used}
        aria-valuemin={0}
        aria-valuemax={validLimit ?? undefined}
        aria-label={inferred ? `${label} (assumed)` : label}
      >
        <div
          className="h-full rounded-full transition-[width,background-color] duration-500"
          style={{
            width: `${pct ?? 0}%`,
            background: fill,
            // Dimming carries the same signal as the tilde for anyone reading
            // the bar rather than the number.
            opacity: inferred ? 0.45 : 1,
          }}
        />
      </div>

      {/* The raw figures stay available but quiet — except for percent limits,
          where "14 of 100 %" only restates the headline. */}
      {validLimit && unit !== "%" && (
        <p className="tnum text-[10px] text-faint">
          {compact(used)} of {compact(validLimit)}
          {unit ? ` ${unit}` : ""}
        </p>
      )}
    </div>
  );
}
